import axios from "axios";
import { baseUrl } from "../../../Baseurl";
import { apiAdapter } from "../../../Utility/secureChannel";
import { markTokenIssued, isTokenLikelyExpired } from "./TokenFreshness";
import { rejectIfTabSessionStale } from "../../../Utility/tabSession";

// Payload/URL/response encryption ab secureAdapter karta hai (ECDH + AES-GCM,
// src/Utility/secureChannel.js) — interceptors me koi encrypt/decrypt nahi.
const axiosInstance = axios.create({
  baseURL: baseUrl,
  withCredentials: true,
  headers: { "X-Requested-With": "XMLHttpRequest" }, // CSRF middleware ke liye zaroori (bina iske cookie wali request 403)
  adapter: apiAdapter, // secure channel + duplicate-submit guard

});

// KSDPL refuses its session cookie (403) unless this header comes with it.
// Plain axios calls to the API get it here; other hosts are left alone.
const API_ORIGIN = baseUrl ? new URL(baseUrl).origin : null;
axios.interceptors.request.use((config) => {
  const target = new URL(axios.getUri(config), window.location.origin);
  if (target.origin === API_ORIGIN) {
    config.headers.set("X-Requested-With", "XMLHttpRequest");
  }
  return config;
});


// ---------------------------------------------------------------------------
// ✅ FIX — "Auth/" ka plain substring-match bahut broad tha. Isse
// "Auth/GetMfaStatus", "Auth/SetupMfa", "Auth/ConfirmMfaSetup",
// "Auth/DisableMfa" jaisi NORMAL protected APIs bhi "auth endpoint"
// samajh li jaati thi (kyunki controller ka naam "Auth" hai), aur unke
// liye 401 aane par refresh-and-retry logic SKIP ho jaata tha — seedha
// "Not Authorized" dikh jaata tha (Security page pe yahi ho raha tha).
//
// Ab hum sirf un chunindaa URLs ko "auth-flow" maante hain jo khud
// login/refresh-cycle ka hissa hain — baaki sab "Auth/" controller ke
// endpoints bhi normal protected API ki tarah treat honge (proactive +
// reactive refresh dono unke liye bhi kaam karenge).
//
// ⚠️ Apne actual login/authenticate endpoint ka exact path yahan zaroor
// confirm/update kar lena (e.g. agar login endpoint "Auth/Login" ki
// jagah "Auth/Authenticate" hai to dono rakh sakte ho, koi problem nahi).
// ---------------------------------------------------------------------------
const AUTH_FLOW_URLS = ["Auth/RefreshToken", "Auth/Authenticate", "Auth/Login"];

function isAuthFlowEndpoint(url) {
  if (!url) return false;
  return AUTH_FLOW_URLS.some((path) => url.includes(path));
}

// ---------------------------------------------------------------------------
// Race-condition-safe refresh queue — jab ek se zyada API calls ek saath
// refresh trigger karein (reactive ho ya proactive), sirf EK refresh call ho,
// baaki sab uska result use karein.
// ---------------------------------------------------------------------------
let isRefreshing = false;
let refreshSubscribers = [];

function subscribeTokenRefresh(callback) {
  refreshSubscribers.push(callback);
}

function onRefreshed() {
  refreshSubscribers.forEach((callback) => callback());
  refreshSubscribers = [];
}

// ---------------------------------------------------------------------------
// Proactive freshness tracking (see ./TokenFreshness.js — shared with
// plainAxios in GetPlainRequestSlice.js so both API surfaces stay in sync
// off a single login/refresh timestamp).
// ---------------------------------------------------------------------------
function ensureFreshToken() {
  if (isRefreshing) {
    return new Promise((resolve) => subscribeTokenRefresh(resolve));
  }
  isRefreshing = true;
  return axiosInstance
    .post("Auth/RefreshToken")
    .then(() => {
      markTokenIssued();
    })
    .catch(() => {
      // Yahan fail hone do — asli request bhi 401 degi, jo neeche wala
      // reactive interceptor already handle karta hai (logout + redirect)
    })
    .finally(() => {
      isRefreshing = false;
      onRefreshed();
    });
}

// ---------------------------------------------------------------------------
// REQUEST — proactive refresh check (race-condition-safe).
// ---------------------------------------------------------------------------
axiosInstance.interceptors.request.use(async (config) => {
  // ✅ FIX — poore "Auth/" controller ki jagah sirf actual auth-flow URLs
  const isAuthEndpoint = isAuthFlowEndpoint(config.url);

  if (!isAuthEndpoint) {
    if (isRefreshing) {
      // Koi aur request pehle se refresh kar rahi hai, uska result use karo
      await new Promise((resolve) => subscribeTokenRefresh(resolve));
    } else if (isTokenLikelyExpired()) {
      // Koi refresh chal nahi rahi, lekin token expire ho chuka — isi request se refresh trigger karo
      await ensureFreshToken();
    }
  }

  return config;
});

// Account switched in another tab — never send this tab's stale data as the
// new account (see Utility/tabSession.js).
axiosInstance.interceptors.request.use(rejectIfTabSessionStale);

// ---------------------------------------------------------------------------
// RESPONSE — token-timestamp tracking + reactive 401-retry (safety net —
// page-reload jaisi cases ke liye jaha proactive check ka pehla-baar pata
// nahi hota).
// ---------------------------------------------------------------------------
axiosInstance.interceptors.response.use(
  (response) => {
    const url = response.config.url || "";

    // Login ya Refresh successful — timer reset karo
    if (url.includes("Authenticate") || url.includes("RefreshToken")) {
      markTokenIssued();
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    // ✅ FIX — yahi wo jagah hai jahan Security page ki APIs
    // ("Auth/GetMfaStatus", "Auth/SetupMfa", etc.) galti se refresh-retry
    // se skip ho rahi thi. Ab sirf asli auth-flow URLs hi skip honge.
    const isAuthEndpoint = isAuthFlowEndpoint(originalRequest?.url);

    // 401 aaya, ye auth-flow endpoint nahi hai, aur ye request pehle retry nahi hui
    if (error.response?.status === 401 && !isAuthEndpoint && !originalRequest._retry) {
      originalRequest._retry = true;

      // Agar koi aur request pehle se refresh kar rahi hai, uska wait karo
      if (isRefreshing) {
        return new Promise((resolve) => {
          subscribeTokenRefresh(() => {
            resolve(axiosInstance(originalRequest));
          });
        });
      }

      isRefreshing = true;
      try {
        await axiosInstance.post("Auth/RefreshToken");
        markTokenIssued();
        isRefreshing = false;
        onRefreshed();
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        // Refresh bhi fail — 9-ghante ki asli (refreshToken) session bhi khatam ho chuki
        isRefreshing = false;
        refreshSubscribers = [];
        localStorage.clear();
        window.location.href = "/";
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;